"use client";

import type { ApiPiece } from "@/hooks/use-projects";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { getPlatformLabel } from "@/components/ui/platform-icon";
import { POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";
import { PieceDetail } from "./piece-detail";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PieceSlideOverProps {
  piece: ApiPiece | null;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (direction: "prev" | "next") => void;
  onApprove?: (pieceId: string) => void;
  onReject?: (pieceId: string) => void;
  onSubmit?: (pieceId: string) => void;
  onRevise?: (pieceId: string) => void;
  onSchedule?: (pieceId: string) => void;
  canApprove: boolean;
  canEdit: boolean;
  canSchedule?: boolean;
  editingField: string | null;
  onEditField: (field: string | null) => void;
  editContent: React.ReactNode;
}

export function PieceSlideOver({
  piece, isOpen, onClose, onNavigate, onApprove, onReject, onSubmit, onRevise, onSchedule,
  canApprove, canEdit, canSchedule, editingField, onEditField, editContent,
}: PieceSlideOverProps) {
  if (!piece) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[480px] overflow-y-auto sm:max-w-[480px]">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onNavigate("prev")}>
              <ChevronLeft size={14} />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onNavigate("next")}>
              <ChevronRight size={14} />
            </Button>
            <SheetTitle className="flex-1 text-sm">
              <span className="font-mono">{piece.shortCode}</span>
              <span className="mx-1.5 text-muted-foreground">·</span>
              <span>
                {piece.postType && POST_TYPE_META[piece.postType as PostType]
                  ? `${getPlatformLabel(POST_TYPE_META[piece.postType as PostType].platform)} ${POST_TYPE_META[piece.postType as PostType].label}`
                  : `${getPlatformLabel(piece.platform)} ${piece.format.toLowerCase()}`}
              </span>
            </SheetTitle>
          </div>
        </SheetHeader>

        <PieceDetail
          piece={piece}
          onApprove={onApprove}
          onReject={onReject}
          onSubmit={onSubmit}
          onRevise={onRevise}
          onSchedule={onSchedule}
          canApprove={canApprove}
          canEdit={canEdit}
          canSchedule={canSchedule}
          editingField={editingField}
          onEditField={onEditField}
          editContent={editContent}
        />
      </SheetContent>
    </Sheet>
  );
}
